import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  Snackbar,
  Alert,
  Paper,
  Box,
  CircularProgress
} from "@mui/material";
import ListLayout from "../../../components/Layout/ListLayout";
import SmtpForm, { SmtpFormValues } from "../../../components/Admin/Smtp/SmtpForm";
import {
  getSmtpConfigByTenant,
  createUpdateSmtpConfigByTenant,
  testSmtpConnectionByTenant,
  deleteSmtpConfigByTenant,
  getDefaultTenant
} from "../../../api/bpm/endpoints";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../auth";
import { useTranslation } from "react-i18next";
import { usePermissions } from "../../../hooks/usePermissions";
import { ConfirmModal } from "../../../components/ConfirmModal";

export const Route = createFileRoute("/__auth/admin/email-config")({
  component: EmailConfigComponent,
  beforeLoad: ({ context }) => {
    if (context.auth.isLoading) return;
    if (!context.auth.user?.isAdmin && !context.auth.hasPermission("tenant.configure_email")) {
      throw redirect({
        to: "/dashboard",
      });
    }
  },
  staticData: {
    title: "routes.emailConfig",
  },
});

function EmailConfigComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentTenant } = useAuth();
  const { isAdmin, hasPermission, isLoading: isLoadingPermissions } = usePermissions();

  const { data: defaultTenantData, isLoading: isLoadingDefaultTenant, isError: isErrorDefaultTenant } = useQuery({
    queryKey: ["default-tenant"],
    queryFn: () => getDefaultTenant().then((res: any) => res?.data || res),
    retry: false,
  });

  const effectiveTenant = (!isLoadingDefaultTenant && !isErrorDefaultTenant && defaultTenantData)
    ? defaultTenantData
    : (currentTenant || "default-domain");

  const queryClient = useQueryClient();

  const [isDeleted, setIsDeleted] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message: string, severity: "success" | "error" = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const { data: smtpConfig, isLoading: isLoadingConfig, error: fetchError, isError: isFetchError, refetch } = useQuery({
    queryKey: ["smtp-config", "tenant", effectiveTenant],
    queryFn: () => getSmtpConfigByTenant(effectiveTenant).then((res: any) => res.data || res),
    enabled: !!effectiveTenant && !isLoadingDefaultTenant && !isDeleted,
    retry: false,
  });

  useEffect(() => {
    if (isFetchError && fetchError) {
      const status = (fetchError as any)?.response?.status;
      if (status !== 404) {
        const errMsg = (fetchError as any)?.response?.data?.message || (fetchError as any)?.message || t("admin.smtp.loadError");
        showSnackbar(errMsg, "error");
      }
    }
  }, [isFetchError, fetchError, t]);

  useEffect(() => {
    if (effectiveTenant) {
      setTestResult(null);
      setIsDeleted(false);
      setFormKey(prev => prev + 1);
      refetch();
    }
  }, [effectiveTenant, refetch]);

  const saveMutation = useMutation({
    mutationFn: (values: SmtpFormValues) => createUpdateSmtpConfigByTenant(effectiveTenant, values as any),
    onSuccess: () => {
      showSnackbar(t("admin.smtp.savedSuccess"));
      setIsDeleted(false);
      queryClient.invalidateQueries({ queryKey: ["smtp-config", "tenant", effectiveTenant] });
      refetch();
    },
    onError: (error: any) => {
      const errMsg = error.response?.data?.message || error.message || t("admin.smtp.saveError");
      showSnackbar(errMsg, "error");
    }
  });

  const testMutation = useMutation({
    mutationFn: (values: SmtpFormValues) => testSmtpConnectionByTenant(effectiveTenant, values as any),
    onSuccess: () => {
      setTestResult({ success: true, message: t("admin.smtp.connectionSuccess") });
    },
    onError: (error: any) => {
      const errMsg = error.response?.data?.message || error.message || t("admin.smtp.connectionError");
      setTestResult({ success: false, message: errMsg });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSmtpConfigByTenant(effectiveTenant),
    onSuccess: () => {
      showSnackbar(t("admin.smtp.deletedSuccess"));
      setIsDeleted(true);
      queryClient.removeQueries({ queryKey: ["smtp-config", "tenant", effectiveTenant] });
      setFormKey(prev => prev + 1);
    },
    onError: (error: any) => {
      const errMsg = error.response?.data?.message || error.message || t("admin.smtp.deleteError");
      showSnackbar(errMsg, "error");
    }
  });

  const handleSubmit = (values: SmtpFormValues) => {
    saveMutation.mutate(values);
  };

  const handleTest = (values: SmtpFormValues) => {
    setTestResult(null);
    testMutation.mutate(values);
  };

  const handleDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const handleCancel = () => {
    navigate({ to: "/dashboard" });
  };

  if (isLoadingPermissions || isLoadingDefaultTenant) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin && !hasPermission("tenant.configure_email")) {
    return null;
  }

  return (
    <ListLayout
      title={t("admin.smtp.title")}
      subtitle={t("admin.smtp.subtitle") || ""}
      isLoading={isLoadingConfig}
    >
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: (theme) => theme.shadows[2], maxWidth: "sm", mx: "auto" }}>
        {isLoadingConfig && !smtpConfig ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <SmtpForm
            key={`${effectiveTenant}-${formKey}`}
            initialData={smtpConfig}
            onSubmit={handleSubmit}
            onTest={handleTest}
            onDelete={handleDelete}
            onCancel={handleCancel}
            isLoading={saveMutation.isPending}
            isTesting={testMutation.isPending}
            isDeleting={deleteMutation.isPending}
            testResult={testResult}
          />
        )}
      </Paper>

      <ConfirmModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={() => {
          deleteMutation.mutate(undefined, {
            onSuccess: () => {
              setDeleteConfirmOpen(false);
            }
          });
        }}
        title={t("common.delete")}
        description={t("admin.smtp.confirmDelete")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        confirmColor="error"
        isLoading={deleteMutation.isPending}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
